/**
 * `<hero-video>` (sections/hero.liquid): the hero's muted background video over its photo.
 *
 * - Picks the phone or the desktop video for the screen (and again when the screen crosses the
 *   breakpoint); HLS first, MP4 for browsers that don't stream HLS.
 * - Loads nothing until the hero is in view, plays only while it is, loops, never with sound.
 * - Never starts on its own with reduced motion; the pause button (WCAG 2.2.2) then offers play.
 * - The photo underneath stays visible until the first frame plays, then the video fades in.
 */

const PHONE = window.matchMedia("(max-width: 47.99rem)");
const CALM = window.matchMedia("(prefers-reduced-motion: reduce)");

class HeroVideo extends HTMLElement {
  #abort = null;
  #observer = null;
  #inView = false;
  #stopped = false;
  #source = "";

  connectedCallback() {
    this.video = this.querySelector("video");
    this.button = this.closest(".hero")?.querySelector("[data-video-toggle]");
    if (!this.video) return;
    this.#stopped = CALM.matches;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.video.addEventListener("playing", () => this.setAttribute("data-playing", ""), { signal });
    this.video.addEventListener("error", () => this.#hideButton(), { signal });
    PHONE.addEventListener("change", () => this.#pick(), { signal });
    this.button?.addEventListener("click", () => {
      this.#stopped = !this.#stopped;
      this.#sync();
    }, { signal });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#inView = entry.isIntersecting;
      if (this.#inView && !this.#source) this.#pick();
      this.#sync();
    }, { threshold: 0.25 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #urls() {
    const phone = PHONE.matches;
    const hls = (phone ? this.dataset.mobileHls : this.dataset.desktopHls) || this.dataset.desktopHls;
    const mp4 = (phone ? this.dataset.mobileMp4 : this.dataset.desktopMp4) || this.dataset.desktopMp4;
    return { hls, mp4 };
  }

  #pick() {
    const { hls, mp4 } = this.#urls();
    const key = `${hls}|${mp4}`;
    if (!hls && !mp4) {
      this.#hideButton();
      return;
    }
    if (key === this.#source) return;
    this.#source = key;
    this.video.replaceChildren(
      ...[
        [hls, "application/vnd.apple.mpegurl"],
        [mp4, "video/mp4"],
      ]
        .filter(([src]) => src)
        .map(([src, type]) => Object.assign(document.createElement("source"), { src, type })),
    );
    this.removeAttribute("data-playing");
    this.video.load();
    if (this.button) this.button.hidden = false;
    this.#sync();
  }

  #sync() {
    if (this.button) {
      const label = this.#stopped ? this.dataset.labelPlay : this.dataset.labelPause;
      this.button.setAttribute("aria-label", label || "");
      this.button.dataset.state = this.#stopped ? "paused" : "playing";
    }
    if (!this.#source) return;
    if (this.#inView && !this.#stopped) {
      this.video.play().catch((error) => {
        // Our own pause() cut in (scrolled away before the first frame): the next sync decides.
        if (error?.name === "AbortError") return;
        // Autoplay refused (e.g. low-power mode): the photo stays, the button offers play.
        this.#stopped = true;
        this.#sync();
      });
    } else {
      this.video.pause();
    }
  }

  #hideButton() {
    if (this.button) this.button.hidden = true;
  }
}

if (!customElements.get("hero-video")) customElements.define("hero-video", HeroVideo);
